/**
 * @fileoverview A utility class that allows creating a Promise with external resolve/reject controls
 *
 * @example
 * // Usage:
 * import { Deferred } from './Deferred'
 *
 * function someAsyncOperation() {
 *   const deferred = new Deferred<string>()
 *
 *   // Can resolve/reject the promise later
 *   setTimeout(() => {
 *     deferred.resolve('completed')
 *   }, 1000)
 *
 *   // Return the promise immediately
 *   return deferred.promise
 * }
 */


/**
 * @class Deferred
 * @description A utility class that allows creating a Promise with external resolve/reject controls
 * @template T - The type of the promise
 */
export class Deferred<T> {
    public readonly promise: Promise<T>
    private resolveFn!: (value: T | PromiseLike<T>) => void
    private rejectFn!: (reason?: unknown) => void

    /**
     * @constructor
     * @description Initializes the Deferred class
     */
    public constructor() {
        this.promise = new Promise<T>((resolve, reject) => {
            this.resolveFn = resolve
            this.rejectFn = reject
        })
    }

    /**
     * @method reject
     * @description Rejects the promise
     * @param {unknown} reason - The reason for the rejection
     */
    public reject(reason?: unknown): void {
        this.rejectFn(reason)
    }

    /**
     * @method resolve
     * @description Resolves the promise
     * @param {T} param - The value to resolve the promise with
     */
    public resolve(param: T): void {
        this.resolveFn(param)
    }
}
